import { ArrowRight } from "lucide-react";
import { useState } from "react";
import { highlightPricingTextFeatures } from "@/components/pricing/pricing_text_highlight";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";

interface ContentItem {
	title: string;
	description: string;
	eligibility: string;
	how: string;
	button: string;
}

const content: Record<string, ContentItem> = {
	english: {
		title: "Price Match Promise",
		description:
			"We are committed to providing the ultimate value. If you find a comparable premium service at a lower investment, we will honor that rate.",
		eligibility:
			"Eligible offers must be from reputable providers offering identical quality, audience retention standards, and delivery speed.",
		how: "Simply contact our concierge with proof (screenshot/link) within 14 days of purchase.",
		button: "Understood",
	},
	hindi: {
		title: "मूल्य मिलान वादा",
		description:
			"हम आपको सर्वोत्तम मूल्य देने के लिए प्रतिबद्ध हैं। यदि आपको कम कीमत पर समान प्रीमियम सेवा मिलती है, तो हम उस दर का सम्मान करेंगे।",
		eligibility:
			"योग्य प्रस्ताव प्रतिष्ठित प्रदाताओं से होने चाहिए जो समान गुणवत्ता, दर्शक प्रतिधारण मानक और वितरण गति प्रदान करते हैं।",
		how: "खरीद के 14 दिनों के भीतर प्रमाण (स्क्रीनशॉट/लिंक) के साथ हमारे कॉन्सियर्ज से संपर्क करें।",
		button: "समझ गया",
	},
	tamil: {
		title: "விலை பொருத்த உறுதிமொழி",
		description:
			"நாங்கள் போட்டி விலைகளை வழங்க உறுதிபூண்டுள்ளோம். தகுதியான சில்லறை விற்பனையாளரிடம் குறைந்த விலையில் ஒரே மாதிரியான தயாரிப்பை நீங்கள் கண்டால், நாங்கள் அதனை பொருத்துவோம்! உங்கள் கொள்முதலின் போது அல்லது கொள்முதல் செய்த 14 நாட்களுக்குள் போட்டியாளரின் தற்போதைய விளம்பர விலையை எங்களுக்குக் காட்டுங்கள்.",
		eligibility:
			"தகுதியான சில்லறை விற்பனையாளர்களில் முக்கிய ஆன்லைன் கடைகள் மற்றும் உள்ளூர் கடைகள் அடங்கும். தயாரிப்பு அதே மாதிரியாக இருக்க வேண்டும் (அதே மாடல், அளவு மற்றும் நிறம்) மற்றும் போட்டியாளரிடம் கையிருப்பில் இருக்க வேண்டும்.",
		how: "விலை பொருத்த கோரிக்கை விடுக்க, குறைந்த விலைக்கான ஆதாரத்துடன் (ஸ்கிரீன்ஷாட் அல்லது விளம்பரம்) எங்கள் வாடிக்கையாளர் சேவையைத் தொடர்பு கொள்ளுங்கள். எங்கள் குழு 24 மணி நேரத்திற்குள் உங்கள் கோரிக்கையை சரிபார்த்து செயல்படுத்தும்.",
		button: "புரிந்தது",
	},
	kannada: {
		title: "ಬೆಲೆ ಹೊಂದಾಣಿಕೆ ಭರವಸೆ",
		description:
			"ನಾವು ಸ್ಪರ್ಧಾತ್ಮಕ ಬೆಲೆಗಳನ್ನು ನೀಡಲು ಬದ್ಧರಾಗಿದ್ದೇವೆ. ಅರ್ಹ ಮಾರಾಟಗಾರರಿಂದ ಕಡಿಮೆ ಬೆಲೆಯಲ್ಲಿ ಒಂದೇ ಉತ್ಪನ್ನವನ್ನು ನೀವು ಕಂಡುಕೊಂಡರೆ, ನಾವು ಅದನ್ನು ಹೊಂದಿಸುತ್ತೇವೆ! ನಿಮ್ಮ ಖರೀದಿಯ ಸಮಯದಲ್ಲಿ ಅಥವಾ ಖರೀದಿಯ ನಂತರ 14 ದಿನಗಳೊಳಗೆ ಸ್ಪರ್ಧಿಯ ಪ್ರಸ್ತುತ ಜಾಹೀರಾತು ಬೆಲೆಯನ್ನು ನಮಗೆ ತೋರಿಸಿ.",
		eligibility:
			"ಅರ್ಹ ಮಾರಾಟಗಾರರಲ್ಲಿ ಪ್ರಮುಖ ಆನ್‌ಲೈನ್ ಅಂಗಡಿಗಳು ಮತ್ತು ಸ್ಥಳೀಯ ಅಂಗಡಿಗಳು ಸೇರಿವೆ. ಉತ್ಪನ್ನವು ಒಂದೇ ರೀತಿಯದಾಗಿರಬೇಕು (ಅದೇ ಮಾದರಿ, ಗಾತ್ರ ಮತ್ತು ಬಣ್ಣ) ಮತ್ತು ಸ್ಪರ್ಧಿಯಲ್ಲಿ ಸ್ಟಾಕ್‌ನಲ್ಲಿರಬೇಕು.",
		how: "ಬೆಲೆ ಹೊಂದಾಣಿಕೆಯನ್ನು ವಿನಂತಿಸಲು, ಕಡಿಮೆ ಬೆಲೆಯ ಪುರಾವೆಯೊಂದಿಗೆ (ಸ್ಕ್ರೀನ್‌ಶಾಟ್ ಅಥವಾ ಜಾಹೀರಾತು) ನಮ್ಮ ಗ್ರಾಹಕ ಸೇವೆಯನ್ನು ಸಂಪರ್ಕಿಸಿ. ನಮ್ಮ ತಂಡವು 24 ಗಂಟೆಗಳೊಳಗೆ ನಿಮ್ಮ ವಿನಂತಿಯನ್ನು ಪರಿಶೀಲಿಸಿ ಪ್ರಕ್ರಿಯೆಗೊಳಿಸುತ್ತದೆ.",
		button: "ಅರ್ಥವಾಯಿತು",
	},
};

export default function PriceMatchDialog() {
	const [isOpen, setIsOpen] = useState(false);
	const [language, setLanguage] = useState("english");

	const currentContent = content[language] || content.english;

	return (
		<>
			<button
				type="button"
				className="group flex w-full max-w-md mx-auto justify-between items-center mb-12 bg-zinc-50 border border-zinc-200 rounded-full px-6 py-3 cursor-pointer hover:bg-zinc-100 hover:border-zinc-300 transition-colors duration-300"
				onClick={() => setIsOpen(true)}
			>
				<div className="flex items-center gap-3">
					<div className="w-2 h-2 rounded-full bg-zinc-400 group-hover:bg-zinc-900 transition-colors" />
					<span className="text-xs font-inter font-medium text-zinc-500 uppercase tracking-widest group-hover:text-zinc-900 transition-colors">
						Price Promise
					</span>
				</div>
				<div className="flex items-center text-xs font-bold font-syne text-zinc-400 group-hover:text-zinc-900 uppercase tracking-wide transition-colors">
					Learn More <ArrowRight className="w-3 h-3 ml-2" />
				</div>
			</button>

			<Dialog open={isOpen} onOpenChange={setIsOpen}>
				<DialogContent className="sm:max-w-[500px] bg-white border border-zinc-100 shadow-2xl rounded-3xl p-6 md:p-8 flex flex-col">
					<DialogHeader className="flex-shrink-0">
						<DialogTitle className="font-syne font-bold text-3xl uppercase tracking-tight text-zinc-900 mb-2">
							{currentContent.title}
						</DialogTitle>

						<div className="flex flex-wrap gap-2 mt-4">
							{["english", "hindi", "tamil", "kannada"].map((lang) => (
								<button
									type="button"
									key={lang}
									onClick={() => setLanguage(lang)}
									className={`
                        px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest transition-colors
                        ${
													language === lang
														? "bg-zinc-900 text-white"
														: "bg-zinc-100 text-zinc-400 hover:bg-zinc-200 hover:text-zinc-600"
												}
                    `}
								>
									{lang}
								</button>
							))}
						</div>
					</DialogHeader>

					<div className="space-y-6 py-4 md:py-6 flex-1 overflow-y-auto min-h-0">
						<p className="font-inter text-sm text-zinc-500 leading-relaxed">
							{highlightPricingTextFeatures(currentContent.description)}
						</p>

						<Separator className="bg-zinc-100" />

						<div>
							<h4 className="font-syne font-bold text-sm uppercase text-zinc-900 mb-2">
								Eligibility
							</h4>
							<p className="font-inter text-sm text-zinc-500 leading-relaxed">
								{highlightPricingTextFeatures(currentContent.eligibility)}
							</p>
						</div>

						<div>
							<h4 className="font-syne font-bold text-sm uppercase text-zinc-900 mb-2">
								How to Request
							</h4>
							<p className="font-inter text-sm text-zinc-500 leading-relaxed">
								{highlightPricingTextFeatures(currentContent.how)}
							</p>
						</div>
					</div>

					<DialogFooter className="flex-shrink-0">
						<Button
							onClick={() => setIsOpen(false)}
							className="w-full bg-zinc-900 text-white font-syne font-bold uppercase tracking-wide rounded-xl h-12 hover:bg-zinc-800"
						>
							{currentContent.button}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}
